<script lang="ts">
  /**
   * FirstRunWizard — DOS boot sequence initialization screen.
   *
   * Single scrolling page styled as a terminal system init.
   * Guides the player through API key setup and controls briefing.
   */
  import "../../styles/wizard.css";
  import { markFirstRunComplete } from "$lib/stores/firstRun";
  import { keyCache } from "$lib/stores/keys";
  import AsciiAnimation from "./AsciiAnimation.svelte";

  interface Props {
    onComplete: () => void;
  }

  let { onComplete }: Props = $props();

  let apiKey = $state("");
  let keyError = $state("");
  let keySaved = $state(false);

  const TITLE_ART = ` ____                     _  __       _
|  _ \\ _ __ ___  __ _  __| |/ _|_   _| |
| | | | '__/ _ \\/ _\` |/ _\` | |_| | | | |
| |_| | | |  __/ (_| | (_| |  _| |_| | |
|____/|_|  \\___|\\__,_|\\__,_|_|  \\__,_|_|

 ____             _   _
|  _ \\  ___ _ __ | |_| |__  ___
| | | |/ _ \\ '_ \\| __| '_ \\/ __|
| |_| |  __/ |_) | |_| | | \\__ \\
|____/ \\___| .__/ \\__|_| |_|___/
           |_|`;

  async function saveApiKey(): Promise<void> {
    keyError = "";
    const trimmed = apiKey.trim();
    if (trimmed.length < 10) {
      keyError = "ERR: Key too short. Paste your full API key.";
      return;
    }
    try {
      await keyCache.store("gemini", trimmed);
      keySaved = true;
    } catch (e) {
      keyError = `ERR: Failed to save key: ${String(e)}`;
    }
  }

  function finish(): void {
    markFirstRunComplete();
    onComplete();
  }
</script>

<div class="frw-root">
  <AsciiAnimation animationLevel="subtle" glyphPercent={8} glyphSpeedMs={350} />
  <div class="wizard">
    <!-- ASCII Title -->
    <pre class="wizard__art">{TITLE_ART}</pre>

    <!-- Version Tag -->
    <div class="wizard__version">SYSTEM INITIALIZATION v1.0</div>
    <div class="wizard__section-rule">===========================</div>

    <!-- Boot Sequence Lines -->
    <div class="wizard__boot-lines">
      <span class="wizard__boot-line wizard__boot-line--ok"
        >{"> Loading narrative engine..........."}</span
      >
      <span class="wizard__boot-line wizard__boot-line--ok"
        >{"> Initializing GURPS mechanics......"}</span
      >
      <span class="wizard__boot-line wizard__boot-line--ok"
        >{"> Calibrating combat subsystem......"}</span
      >
      <span class="wizard__boot-line wizard__boot-line--ok"
        >{"> Mapping dimensional rift..........."}</span
      >
      <span class="wizard__boot-line wizard__boot-line--ok"
        >{"> Seeding world generator..........."}</span
      >
      <span class="wizard__boot-line wizard__boot-line--ok"
        >{"> Scanning for arcane signals......."}</span
      >
    </div>

    <!-- CONFIGURATION Section -->
    <div class="wizard__section wizard__section--config">
      <div class="wizard__section-title">Configuration</div>
      <div class="wizard__section-rule">{"============="}</div>

      <p class="wizard__text">The AI narrator requires an API key to generate your story.</p>
      <p class="wizard__text">A free Gemini key is all you need to play.</p>

      <ol class="wizard__instructions">
        <li>
          Visit <a
            href="https://aistudio.google.com/apikey"
            target="_blank"
            rel="noopener noreferrer">aistudio.google.com/apikey</a
          >
        </li>
        <li>Sign in with Google</li>
        <li>Click "Create API key"</li>
        <li>Paste below</li>
      </ol>

      {#if keySaved}
        <p class="wizard__key-configured">KEY CONFIGURED <span class="wizard__cursor"></span></p>
      {:else}
        <div class="wizard__key-row">
          <span class="wizard__key-prompt">&gt; API KEY:&nbsp;</span>
          <input
            class="wizard__key-input"
            type="password"
            placeholder="AIzaSy..."
            bind:value={apiKey}
            onkeydown={(e) => {
              if (e.key === "Enter") saveApiKey();
            }}
          />
          <button class="wizard__key-btn" onclick={saveApiKey}>Save</button>
        </div>
      {/if}

      {#if keyError}
        <p class="wizard__error">{keyError}</p>
      {/if}
      {#if keySaved}
        <p class="wizard__success">Gemini key stored successfully.</p>
      {/if}

      <p class="wizard__text--dim">
        Other providers (Claude, OpenAI, Fireworks, DeepSeek, Together) can be configured later in
        Settings (?).
      </p>
    </div>

    <!-- OPERATOR BRIEFING Section -->
    <div class="wizard__section wizard__section--briefing">
      <div class="wizard__section-title">Operator Briefing</div>
      <div class="wizard__section-rule">{"================="}</div>

      <table class="wizard__briefing">
        <tbody>
          <tr>
            <td>CHOICES</td>
            <td>A / S / D / F</td>
            <td>direct select, no Enter</td>
          </tr>
          <tr>
            <td>TEXT</td>
            <td>Hold Space</td>
            <td>accelerate typewriter</td>
          </tr>
          <tr>
            <td></td>
            <td>Press Enter</td>
            <td>skip to end</td>
          </tr>
          <tr>
            <td>OVERLAYS</td>
            <td>M / T / R / J</td>
            <td>Map, Timeline, Rel, Journal</td>
          </tr>
          <tr>
            <td>SYSTEM</td>
            <td>? = Settings</td>
            <td>Q = Quit</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- DESCEND Button -->
    <div class="wizard__section wizard__section--launch">
      <div class="wizard__launch-wrapper">
        <div class="wizard__launch-rule">{"===================================="}</div>
        <button
          class="wizard__launch-btn"
          class:wizard__launch-btn--active={keySaved}
          onclick={finish}
        >
          Descend into the Depths
        </button>
        <div class="wizard__launch-rule">{"===================================="}</div>
      </div>
    </div>
  </div>
</div>

<style>
  .frw-root {
    position: relative;
    min-height: 100vh;
    min-height: 100dvh;
  }
</style>
